import { useContext, useState, useEffect } from "react";
import { AccountContext } from "@/state/providers/AccountProvider";
import { useBillingPlan, useCreatePortalSession } from "@/state/hooks/useBilling";
import { formatCurrency, formatDate } from "@/features/settings/components/billing/billingUtils";

// TEMP: billing banner disabled for now. Flip back to `true` to re-enable the
// top-of-app unpaid-invoice banner (no other changes needed). See PR disabling it.
// Exported so the test suite can gate behavior coverage on the flag.
export const BANNER_ENABLED = false;

const DISMISS_KEY = "billing_banner_dismissed";
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

function isDismissedRecently(): boolean {
    try {
        const dismissedAt = localStorage.getItem(DISMISS_KEY);
        if (!dismissedAt) return false;
        return Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION;
    } catch (err) {
        console.warn("Could not read banner dismissal from localStorage", err);
        return false;
    }
}

export default function UnpaidInvoiceBanner() {
    // Use useContext directly instead of useAccountContext to avoid throwing
    // when the banner is rendered outside the AccountProvider (e.g., before auth)
    const accountContext = useContext(AccountContext);
    const accountData = accountContext?.accountData;
    const hasAccount = !!accountData && "id" in accountData;
    const { data: billingPlan } = useBillingPlan({ enabled: hasAccount });
    const portalSession = useCreatePortalSession();
    const [isDismissed, setIsDismissed] = useState(() => isDismissedRecently());

    // Re-check dismissal on mount in case localStorage changed in another tab
    useEffect(() => {
        setIsDismissed(isDismissedRecently());
    }, []);

    // TEMP: banner disabled via flag above. Kept after the hooks so hook order
    // stays stable when the flag is flipped back on.
    if (!BANNER_ENABLED) return null;

    if (!hasAccount) return null;

    // Group and trial accounts don't have invoices to pay
    if (billingPlan?.billing_type === "group" || billingPlan?.billing_type === "trial") return null;

    // Only show when there is an unpaid invoice
    const unpaidInvoice = billingPlan?.unpaid_invoice;
    if (!unpaidInvoice) return null;

    // Respect the 7-day dismissal window
    if (isDismissed) return null;

    const amountStr = formatCurrency(unpaidInvoice.amount_due, unpaidInvoice.currency);
    const dateStr = formatDate(unpaidInvoice.due_date);
    const message = dateStr
        ? `You have an unpaid invoice for ${amountStr} due ${dateStr}.`
        : `You have an unpaid invoice for ${amountStr}.`;

    function handleDismiss(): void {
        try {
            localStorage.setItem(DISMISS_KEY, Date.now().toString());
        } catch (err) {
            console.warn("Could not persist banner dismissal to localStorage", err);
        }
        setIsDismissed(true);
    }

    return (
        <div className="bg-[#FE2962] text-white px-4 py-3 flex items-center justify-between">
            <p className="text-sm font-medium">{message}</p>
            <div className="flex items-center gap-3 shrink-0 ml-4">
                <button
                    type="button"
                    onClick={() => portalSession.mutate()}
                    disabled={portalSession.isPending}
                    className="text-sm font-medium bg-white text-[#FE2962] px-3 py-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-60"
                >
                    Pay Now
                </button>
                <button
                    type="button"
                    className="text-sm font-medium text-white/80 hover:text-white transition-colors"
                    onClick={handleDismiss}
                >
                    Dismiss
                </button>
            </div>
        </div>
    );
}
